import React from 'react';
import { TrendingUp, TrendingDown, Star, Eye, ShoppingCart, Target, BarChart3 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface MarketplaceCardProps {
  bot: any;
  onBuy: (bot: any) => void;
}

const MarketplaceCard: React.FC<MarketplaceCardProps> = ({ bot, onBuy }) => {
  const navigate = useNavigate();

  const profit = bot.performance_stats?.total_profit_pct || 0;
  const winRate = bot.performance_stats?.win_rate_pct || 0;
  const totalTrades = bot.performance_stats?.total_trades || 0;
  const rating = bot.rating_avg || 0;
  const views = bot.views_count || 0;
  const purchases = bot.purchases_count || 0;

  const getCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      'trend-following': 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
      'scalping': 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
      'swing-trading': 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
      'support-resistance': 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
      'general': 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
    };
    return colors[category] || colors.general;
  };

  return (
    <div className="group bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 overflow-hidden hover:shadow-2xl hover:scale-[1.02] transition-all duration-300">
      {/* Preview Image */}
      <div className="relative h-48 bg-gradient-to-br from-blue-500 via-purple-500 to-pink-500 overflow-hidden">
        {bot.preview_image_url ? (
          <img
            src={bot.preview_image_url}
            alt={bot.title}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <BarChart3 className="w-20 h-20 text-white/30" />
          </div>
        )}

        {/* Category Badge */}
        <div className="absolute top-3 left-3">
          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getCategoryColor(bot.category)}`}>
            {bot.category?.replace('-', ' ').replace(/\b\w/g, (l: string) => l.toUpperCase()) || 'General'}
          </span>
        </div>

        {/* Rating Badge */}
        {rating > 0 && (
          <div className="absolute top-3 right-3 flex items-center gap-1 bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm px-2 py-1 rounded-full">
            <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
            <span className="text-xs font-semibold text-gray-900 dark:text-white">
              {rating.toFixed(1)}
            </span>
          </div>
        )}

        {/* Stats Overlay */}
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3">
          <div className="flex items-center gap-4 text-white text-xs">
            <div className="flex items-center gap-1">
              <Eye className="w-3 h-3" />
              <span>{views}</span>
            </div>
            <div className="flex items-center gap-1">
              <ShoppingCart className="w-3 h-3" />
              <span>{purchases}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-5">
        {/* Title */}
        <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2 line-clamp-2 min-h-[56px]">
          {bot.title}
        </h3>

        {/* Description */}
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 line-clamp-2 min-h-[40px]">
          {bot.description || 'No description provided'}
        </p>

        {/* Performance Stats */}
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-3 text-center">
            <div className="flex items-center justify-center mb-1">
              {profit >= 0 ? (
                <TrendingUp className="w-4 h-4 text-green-600" />
              ) : (
                <TrendingDown className="w-4 h-4 text-red-600" />
              )}
            </div>
            <div className={`text-lg font-bold ${profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {profit >= 0 ? '+' : ''}{profit.toFixed(1)}%
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400">Profit</div>
          </div>

          <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-3 text-center">
            <div className="flex items-center justify-center mb-1">
              <Target className="w-4 h-4 text-blue-600" />
            </div>
            <div className="text-lg font-bold text-gray-900 dark:text-white">
              {winRate.toFixed(0)}%
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400">Win Rate</div>
          </div>

          <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-3 text-center">
            <div className="flex items-center justify-center mb-1">
              <BarChart3 className="w-4 h-4 text-purple-600" />
            </div>
            <div className="text-lg font-bold text-gray-900 dark:text-white">
              {totalTrades}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400">Trades</div>
          </div>
        </div>

        {/* Tags */}
        {bot.tags && bot.tags.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4">
            {bot.tags.slice(0, 3).map((tag: string, index: number) => (
              <span
                key={index}
                className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md text-xs"
              >
                #{tag}
              </span>
            ))}
          </div>
        )}

        {/* Price & Actions */}
        <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
          <div>
            <div className="text-sm text-gray-500 dark:text-gray-400">Price</div>
            <div className="text-2xl font-bold text-gray-900 dark:text-white">
              ${bot.price_usd}
            </div>
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => navigate(`/marketplace/${bot.id}`)}
              className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 font-medium transition-colors"
            >
              View Details
            </button>
            <button
              onClick={() => onBuy(bot)}
              className="px-4 py-2 rounded-lg bg-gradient-to-r from-blue-600 to-purple-600 text-white font-medium hover:from-blue-700 hover:to-purple-700 transition-all flex items-center gap-2"
            >
              <ShoppingCart className="w-4 h-4" />
              Buy
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MarketplaceCard;
